import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppRoute, UserRole } from '../api/dtos/user.dto';

interface AuthState {
  // State
  token: string | null;
  userId: string | null;
  username: string | null;
  userRole: UserRole | null;
  allowedRoutes: AppRoute[];
  isAuthenticated: boolean;

  // Actions
  setAuth: (
    token: string,
    userId: string,
    username: string,
    role: UserRole,
    routes: AppRoute[]
  ) => void;
  clearAuth: () => void;
  updateAllowedRoutes: (routes: AppRoute[]) => void;
  updateUserRole: (role: UserRole) => void;
  updateToken: (token: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      userId: null,
      username: null,
      userRole: null,
      allowedRoutes: [],
      isAuthenticated: false,

      setAuth: (token, userId, username, userRole, allowedRoutes) => {
        set({
          token,
          userId,
          username,
          userRole,
          allowedRoutes,
          isAuthenticated: !!(token || userId),
        });
      },

      clearAuth: () => {
        // Clean up legacy per-key storage from pre-Wave 14d migration
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('userRole');
          localStorage.removeItem('allowedRoutes');
          sessionStorage.removeItem('userId');
          sessionStorage.removeItem('userRole');
          sessionStorage.removeItem('allowedRoutes');
        } catch {
          // Ignore storage errors (SSR, private browsing, etc.)
        }
        set({
          token: null,
          userId: null,
          username: null,
          userRole: null,
          allowedRoutes: [],
          isAuthenticated: false,
        });
      },

      updateAllowedRoutes: (routes) =>
        set({ allowedRoutes: routes }),

      updateUserRole: (role) =>
        set({ userRole: role }),

      updateToken: (token) =>
        set((state) => ({ token, isAuthenticated: !!(token || state.userId) })),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        userId: state.userId,
        username: state.username,
        userRole: state.userRole,
        allowedRoutes: state.allowedRoutes,
      }),
    }
  )
);

// ── Cross-tab sync ─────────────────────────────────────────
// When another tab modifies `auth-storage`, rehydrate this tab's store.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'auth-storage') {
      if (event.newValue === null) {
        // Storage was cleared in another tab → log out here too
        useAuthStore.setState({
          token: null,
          userId: null,
          username: null,
          userRole: null,
          allowedRoutes: [],
          isAuthenticated: false,
        });
      } else {
        try {
          const { state: persisted } = JSON.parse(event.newValue);
          if (persisted) {
            useAuthStore.setState(persisted);
          }
        } catch {
          // Ignore parse errors
        }
      }
    }
  });
}

// Selector hooks for performance optimization
// isAuthenticated is derived from token/userId — never stale
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useToken = () => useAuthStore((state) => state.token);
export const useUserId = () => useAuthStore((state) => state.userId);
export const useUsername = () => useAuthStore((state) => state.username);
export const useUserRole = () => useAuthStore((state) => state.userRole);
export const useAllowedRoutes = () => useAuthStore((state) => state.allowedRoutes);
